import PropTypes from "prop-types";
import React from "react";
import Date from "../../../common/controls/Date";
import Select from "../../../common/controls/Select";

const quarters = {
    start: [
        {m: "01", d: "01", label: "Q1 (Jan 1)"},
        {m: "04", d: "01", label: "Q2 (Apr 1)"},
        {m: "07", d: "01", label: "Q3 (Jul 1)"},
        {m: "10", d: "01", label: "Q4 (Oct 1)"},
    ],
    end: [
        {m: "03", d: "31", label: "Q1 (Mar 31)"},
        {m: "06", d: "30", label: "Q2 (Jun 30)"},
        {m: "09", d: "30", label: "Q3 (Sep 30)"},
        {m: "12", d: "31", label: "Q4 (Dec 31)"},
    ]
};
const allQuarters = [...quarters.start, ...quarters.end];

export const isDateRangeValid = (start, end) => {
    if (!start) {
        return false;
    }
    if (!end) {
        return false;
    }
    return start <= end;

};

function zeroPad(it, len) {
    it = it.toString();
    if (it.length === 0) return it;
    while (it.length < len) it = `0${it}`;
    return it;
}

export const dateToString = (v) => {
    if(typeof v === "string") {
        return v;
    }
    if (typeof v === "object") {
        if (typeof v.format === "function") {
            return v.format("YYYY-MM-DD");
        }
        if (typeof v.getDate === "function") {
            return [
                zeroPad(v.getFullYear(), 4),
                zeroPad(v.getMonth() + 1, 2),
                zeroPad(v.getDate(), 2)
            ].join("-");
        }
    }
    if (Array.isArray(v)) {
        return v.join("-");
    }
    return ""; // not sure what we got for a date.
};

const getYMD = (v) => {
    let parts;
    if(typeof v === "string") {
        let [y, m, d] = v.split("-");
        y = parseInt(y, 10);
        m = parseInt(m, 10);
        d = parseInt(d, 10);
        parts = [y, m, d];
    }
    else if (v instanceof Date) {
        parts = [v.getFullYear(), v.getMonth() + 1, v.getDate()];
    }
    else if (Array.isArray(v)) {
        parts = [v[0], v[1], v[2]];
    } else {
        parts = ["","",""]; // not sure what we got for a date.
    }
    return [
        zeroPad(parts[0], 4),
        zeroPad(parts[1], 2),
        zeroPad(parts[2], 2),
    ];
};

const getQuarter = (v) => {
    // returns the valid quarter value or false;
    if(!v) return false;
    const [, m, d] = getYMD(v);
    for(const i in allQuarters) {
        if(allQuarters[i].m === m && allQuarters[i].d === d) {
            return v;
        }
    }
    return false;
};

const isQuarter = (v) => !!getQuarter(v);

function dissectValue(value) {
    let year = "", month = "", day = "", monthDay = "";
    if(isQuarter(value)) {
        [year, month, day] = getYMD(value);
        monthDay = month + "-" + day;
    }
    return {y: year, md: monthDay};
}

const QuarterSelect = ({
                           options,
                           value,
                           min,
                           max,
                           onChange,
                           disabled,
                           required = false,
                           ...props
                       }) => {
    const [ state, setState ] = React.useState(() => dissectValue(value));
    React.useEffect(
        () => setState(dissectValue(value)),
        [ value ],
    );
    const handleChange = (key, value) => {
        // construct date from quarter + year
        let nextState;
        if (key === "quarter") {
            nextState = { ...state, md: value };
        } else {
            nextState = { ...state, y: value };
        }
        if (!!nextState.y && !!nextState.md) {
            onChange(nextState.y + "-" + nextState.md);
        } else {
            onChange("");
        }
        setState(nextState);
    };
    const minYear = min[0], maxYear = max[0];
    const years = [];
    for(let i = maxYear; i >= minYear; i--) {
        years.push(i.toString());
    }

    const quarterOptions = options.map((o) => ({label: o.label, value: `${o.m}-${o.d}`}));
    quarterOptions.unshift({label: "", value: ""});
    const yearOptions = years.map((y) => ({label: y, value: y}));
    yearOptions.unshift({label: "", value: ""});

    return (
        <React.Fragment>
            <div className="col-sm-7">
                <Select
                    value={state.md}
                    width="100%"
                    className="quarter_select quarter_from partdep form-control"
                    disabled={disabled}
                    onCommitChange={(v) => handleChange("quarter", v)}
                    options={quarterOptions}
                    required={required}
                />
            </div>
            <div className="col-sm-5">
                <Select
                    value={state.y}
                    className="year_select year_from partdep form-control"
                    disabled={disabled}
                    onCommitChange={(v) => handleChange("year", v)}
                    options={yearOptions}
                    required={required}
                />
            </div>
        </React.Fragment>
    );
};

QuarterSelect.propTypes = {
    options: PropTypes.array,
    value: PropTypes.string,
    min: PropTypes.array,
    max: PropTypes.array,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    required: PropTypes.bool,
};

const DateRange = ({
                       start,
                       end,
                       min,
                       max,
                       onChange,
                       expanded = false,
                       disabled = true,
                       required = false,
                   }) => {
    const [ byDate, setByDate ] = React.useState(!!start || !!end ? !isQuarter(
        start) && !isQuarter(end) : false);
    const handleChange = (key, value) => {
        if (byDate) {
            value = value ? value.format("YYYY-MM-DD") : "";
        }
        onChange(key, value);
    };

    return (
        <React.Fragment>
            <ul className="nav nav-tabs">
                <li role="presentation" className={!byDate ? "active": ""}>
                    <a
                        href="#"
                        className="dateSelect"
                        data-role="quarter"
                        onClick={(e) => {e.preventDefault(); setByDate(false);}}
                    >
                        By Quarter
                    </a>
                </li>
                <li role="presentation" className={byDate ? "active": ""}>
                    <a
                        href="#"
                        className="dateSelect"
                        data-role="date"
                        onClick={(e) => {e.preventDefault(); setByDate(true);}}
                    >
                        By Date
                    </a>
                </li>
            </ul>
            <br/>
            { !!byDate ? (
                <div className="dateFields export-datecontrols">
                    <div className="form-group">
                        <div className="col-sm-12">
                            <div id="date_from" className="date partdep">
                                <Date
                                    value={start}
                                    disabled={disabled}
                                    onCommitChange={(v) =>
                                        handleChange("start", v)}
                                    required={required}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="form-group">
                        <div className="col-sm-12">
                            <div id="date_to" className="date partdep">
                                <Date
                                    value={end}
                                    disabled={disabled}
                                    onCommitChange={(v) =>
                                        handleChange("end", v)}
                                    required={required}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="quarterFields export-datecontrols">
                    <div className="form-group row">
                        <QuarterSelect
                            options={quarters.start}
                            value={start}
                            min={min}
                            max={max}
                            onChange={(v) => handleChange("start", v)}
                            disabled={disabled}
                            required={required}
                        />
                    </div>
                    <div className="form-group row">
                        <QuarterSelect
                            options={quarters.end}
                            value={end}
                            min={min}
                            max={max}
                            onChange={(v) => handleChange("end", v)}
                            disabled={disabled}
                            required={required}
                        />
                    </div>
                </div>
            )}
            {start && end && !isDateRangeValid(start, end) && <div
                className={"alert alert-danger after-form-group"}
            >
                {byDate
                    ? "Start date cannot be after end date."
                    : "Start quarter cannot be after end quarter."}
            </div>}
        </React.Fragment>
    );
};

DateRange.propTypes = {
    start: PropTypes.string,
    end: PropTypes.string,
    min: PropTypes.array,
    max: PropTypes.array,
    onChange: PropTypes.func,
    expanded: PropTypes.bool,
    disabled: PropTypes.bool,
    required: PropTypes.bool,
};

export default DateRange;



// WEBPACK FOOTER //
// ./app/lib/hex/views/components/DateRange.js